import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './ProductDetail.module.css';
import API_BASE_URL from '../../config/api';

const ProductDetail = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    const fetchProduct = async () => {
      try {
        const lang = i18n.language === 'ar' ? 'ar' : 'en';
        const res = await fetch(`${API_BASE_URL}/products/${id}?lang=${lang}`, {
          signal: controller.signal,
        });

        if (!res.ok) {
          setProduct(null);
          return;
        }

        const data = await res.json();

        const galleryImages = (data.images || []).map((img) => ({
          url: img.url,
          alt: img.alt || data.title,
          isMain: img.is_main,
        }));

        if (galleryImages.length === 0 && data.image_url) {
          galleryImages.push({ url: data.image_url, alt: data.title, isMain: true });
        }

        setProduct({
          id: data.slug,
          title: data.title,
          sections: data.sections || [],
          fullDescription: data.full_description || data.fullDescription || '',
          imageUrl: data.image_url,
          images: galleryImages,
        });
        setSelectedImage(0);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to load product', error);
        }
      }
    };

    fetchProduct();

    return () => controller.abort();
  }, [id, i18n.language]);

  if (!product) {
    return (
      <div className={styles.productDetailPage}>
        <div className={styles.container}>
          <div className={styles.notFound}>
            <h1>Product Not Found</h1>
            <Link to="/products" className={styles.backBtn}>
              Back to Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const galleryImages = product.images || [];
  const hasMultipleImages = galleryImages.length > 1;
  const hasImages = galleryImages.length > 0;

  const hasSections = product.sections && Array.isArray(product.sections) && product.sections.length > 0;

  return (
    <div className={styles.productDetailPage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <Link to="/products" className={styles.backLink}>
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            <span>{t('products.title')}</span>
          </Link>
          <h1 className={styles.heroTitle}>{product.title}</h1>
          <div className={styles.heroUnderline}></div>
        </div>
      </section>

      {/* Product Detail Section */}
      <section className={styles.detailSection}>
        <div className={styles.container}>
          {/* Images Section - Top */}
          {hasImages && (
            <div className={styles.imagesSection}>
              {/* Main Image */}
              <div className={styles.mainImageContainer}>
                <div className={styles.mainImageWrapper}>
                  <img
                    src={galleryImages[selectedImage]?.url}
                    alt={galleryImages[selectedImage]?.alt || product.title}
                    className={styles.mainImage}
                    onClick={() => hasMultipleImages && setIsGalleryOpen(true)}
                    style={hasMultipleImages ? { cursor: 'pointer' } : {}}
                  />
                  {hasMultipleImages && (
                    <button
                      className={styles.viewGalleryBtn}
                      onClick={() => setIsGalleryOpen(true)}
                      aria-label="View Gallery"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="7" height="7" />
                        <rect x="14" y="3" width="7" height="7" />
                        <rect x="14" y="14" width="7" height="7" />
                        <rect x="3" y="14" width="7" height="7" />
                      </svg>
                      <span>View All Images ({galleryImages.length})</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Thumbnail Grid */}
              {hasMultipleImages && (
                <div className={styles.thumbnailGrid}>
                  {galleryImages.map((img, index) => (
                    <button
                      key={index}
                      className={`${styles.thumbnailItem} ${selectedImage === index ? styles.active : ''}`}
                      onClick={() => setSelectedImage(index)}
                      aria-label={`View image ${index + 1}`}
                    >
                      <img src={img.url} alt={img.alt || `${product.title} ${index + 1}`} />
                      <div className={styles.thumbnailOverlay}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                          <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Content Sections - Grid Layout */}
          <div className={styles.contentGrid}>
            {hasSections ? (
              <>
                {product.sections.map((section, index) => (
                  <div key={index} className={styles.infoCard}>
                    <h2 className={styles.sectionTitle}>{section.title}</h2>
                    <div
                      className={styles.description}
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </div>
                ))}
              </>
            ) : (
              product.fullDescription && (
                <div className={styles.infoCard}>
                  <h2 className={styles.sectionTitle}>{t('products.productDescription')}</h2>
                  <div
                    className={styles.description}
                    dangerouslySetInnerHTML={{ __html: product.fullDescription }}
                  />
                </div>
              )
            )}

            {/* Contact CTA - Full Width */}
            <div className={`${styles.ctaCard} ${styles.ctaFullWidth}`}>
              <div className={styles.ctaIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <h3 className={styles.ctaTitle}>Interested in this product?</h3>
              <p className={styles.ctaText}>Contact us for more information and custom solutions</p>
              <Link to="/contact-us" className={styles.contactBtn}>
                Contact Us
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Full Screen Gallery Modal */}
      {isGalleryOpen && hasMultipleImages && (
        <div className={styles.galleryModal} onClick={() => setIsGalleryOpen(false)}>
          <button
            className={styles.closeGallery}
            onClick={() => setIsGalleryOpen(false)}
            aria-label="Close Gallery"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          
          <div className={styles.galleryContent} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.galleryNav}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage((prev) => (prev > 0 ? prev - 1 : galleryImages.length - 1));
              }}
              aria-label="Previous Image"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            
            <div className={styles.galleryImageContainer}>
              <img
                src={galleryImages[selectedImage]?.url}
                alt={galleryImages[selectedImage]?.alt || `${product.title} ${selectedImage + 1}`}
                className={styles.galleryMainImage}
              />
            </div>
            
            <button
              className={styles.galleryNav}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage((prev) => (prev < galleryImages.length - 1 ? prev + 1 : 0));
              }}
              aria-label="Next Image"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          <div className={styles.galleryFooter} onClick={(e) => e.stopPropagation()}>
            <div className={styles.galleryCounter}>
              {selectedImage + 1} / {galleryImages.length}
            </div>
            <div className={styles.galleryThumbnails}>
              {galleryImages.map((img, index) => (
                <button
                  key={index}
                  className={`${styles.galleryThumbnail} ${selectedImage === index ? styles.active : ''}`}
                  onClick={() => setSelectedImage(index)}
                  aria-label={`Go to image ${index + 1}`}
                >
                  <img src={img.url} alt={img.alt || `${product.title} ${index + 1}`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
